import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Calendar, Users, Clock, Sparkles, FileText, Bell, CheckSquare, Pencil, Trash2 } from "lucide-react";

// Fallback om /api/subjects inte svarar — håller dialogen användbar.
const FALLBACK_SUBJECTS = [
  "Svenska", "Engelska", "Matematik", "Biologi", "Fysik", "Kemi",
  "Historia", "Geografi", "Samhällskunskap", "Religionskunskap",
  "Bild", "Musik", "Idrott och hälsa", "Slöjd",
  "Hem- och konsumentkunskap", "Teknik", "Moderna språk", "Övrigt",
];

const WEEKDAY_LABELS = ["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"];

function todayIso(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

export function AddHomeAssignmentDialog({ open, onClose, onSaved, availableChildren, editAssignmentId = null, defaultKind = "homework" }) {
  const [editingId, setEditingId] = useState(null);
  const [existing, setExisting] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("Svenska");
  const [kind, setKind] = useState(defaultKind);
  const [childIds, setChildIds] = useState([]);
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso(7));
  const [weekdays, setWeekdays] = useState([0, 1, 2, 3]); // mån–tor default
  const [durationMin, setDurationMin] = useState(20);
  const [requiresSubmission, setRequiresSubmission] = useState(false);
  const [reminderPush, setReminderPush] = useState(true);
  const [autoQuiz, setAutoQuiz] = useState(true);
  const [freeText, setFreeText] = useState("");
  const [saving, setSaving] = useState(false);
  const [createdAssignmentId, setCreatedAssignmentId] = useState(null);
  const [createdEventId, setCreatedEventId] = useState(null);
  const [subjects, setSubjects] = useState(
    FALLBACK_SUBJECTS.map((n) => ({ code: n.slice(0, 2).toUpperCase(), name: n })),
  );

  // Hämta ämneskatalogen från /api/subjects när dialogen öppnas
  useEffect(() => {
    if (!open) return;
    api.get("/subjects")
      .then((r) => {
        const items = (r.data?.items || []).filter((s) => s.code && s.name);
        if (items.length) {
          const sorted = [...items].sort((a, b) => a.name.localeCompare(b.name, "sv"));
          setSubjects([...sorted, { code: "OV", name: "Övrigt" }]);
        }
      })
      .catch(() => { /* behåller fallback */ });
  }, [open]);

  useEffect(() => {
    if (!open) {
      // reset on close
      setTitle(""); setSubject("Svenska"); setChildIds([]); setKind(defaultKind);
      setStartDate(todayIso()); setEndDate(todayIso(7));
      setWeekdays([0, 1, 2, 3]); setDurationMin(20);
      setRequiresSubmission(false); setReminderPush(true);
      setAutoQuiz(true); setFreeText("");
      setCreatedAssignmentId(null); setCreatedEventId(null);
      setEditingId(null); setExisting([]);
    } else {
      // När dialogen öppnas, synka kind med defaultKind (kan ha ändrats om
      // föräldra-komponenten triggade "+ Prov" eller "+ Hemarbete")
      setKind(defaultKind);
      // Ladda befintliga hemarbeten när dialogen öppnas
      (async () => {
        setLoadingList(true);
        try {
          const { data } = await api.get("/home-assignments");
          const items = data?.items || [];
          setExisting(items);
          // Om openrequested för en specifik post (från Läxor-listans pencil)
          if (editAssignmentId) {
            const target = items.find((x) => x.id === editAssignmentId);
            if (target) loadForEdit(target);
          }
        } catch {
          setExisting([]);
        } finally {
          setLoadingList(false);
        }
      })();
    }
  }, [open, editAssignmentId, defaultKind]);

  const loadForEdit = (asg) => {
    setEditingId(asg.id);
    setTitle(asg.title || "");
    setSubject(asg.subject || "Svenska");
    setKind(asg.kind || "homework");
    setChildIds(asg.child_user_ids || []);
    setStartDate(asg.start_date || todayIso());
    setEndDate(asg.end_date || todayIso(7));
    setWeekdays(asg.weekdays || [0, 1, 2, 3]);
    setDurationMin(asg.duration_min || 20);
    setRequiresSubmission(!!asg.requires_submission);
    setReminderPush(asg.reminder_push !== false);
    setAutoQuiz(asg.auto_quiz !== false);
    setFreeText(asg.free_text || "");
    setCreatedAssignmentId(null);
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const handleDelete = async (asg) => {
    // Tvåstegs-confirm utan native window.confirm (som ofta sväljs i PWA).
    if (confirmDeleteId !== asg.id) {
      setConfirmDeleteId(asg.id);
      // Auto-reset efter 4s om användaren ångrar sig och klickar bort
      setTimeout(() => {
        setConfirmDeleteId((prev) => (prev === asg.id ? null : prev));
      }, 4000);
      return false;
    }
    try {
      await api.delete(`/home-assignments/${asg.id}`);
      toast.success(`"${asg.title}" raderat`);
      setExisting((prev) => prev.filter((x) => x.id !== asg.id));
      setConfirmDeleteId(null);
      if (onSaved) onSaved();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte radera");
      setConfirmDeleteId(null);
      return false;
    }
  };

  const toggleChild = (id) => {
    setChildIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleWeekday = (i) => {
    setWeekdays((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i].sort());
  };

  const canSave =
    title.trim() &&
    childIds.length > 0 &&
    (kind === "test" ? !!startDate : (weekdays.length > 0 && startDate && endDate));

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      // För prov: end_date = start_date, weekdays auto från datum
      // (backend kräver minst 1 weekday för expansion av events_cache)
      let effectiveEndDate = endDate;
      let effectiveWeekdays = weekdays;
      if (kind === "test") {
        effectiveEndDate = startDate;
        const dt = new Date(startDate);
        // JS Date.getDay: 0=sön…6=lör. Backend: 0=mån…6=sön.
        const jsDay = dt.getDay();
        effectiveWeekdays = [jsDay === 0 ? 6 : jsDay - 1];
      }
      const body = {
        title: title.trim(),
        subject,
        kind,
        child_user_ids: childIds,
        start_date: startDate,
        end_date: effectiveEndDate,
        weekdays: effectiveWeekdays,
        duration_min: Number(durationMin) || 20,
        requires_submission: requiresSubmission,
        reminder_push: reminderPush,
        free_text: freeText.trim(),
        auto_quiz: autoQuiz,
      };
      let data;
      if (editingId) {
        const res = await api.patch(`/home-assignments/${editingId}`, body);
        data = res.data;
        toast.success(
          data.schedule_changed
            ? `Uppdaterat — ${data.events_created} ${kind === "test" ? "prov-tillfällen" : "läxor"} återgenererade`
            : (kind === "test" ? "Prov uppdaterat" : "Hemarbete uppdaterat"),
        );
        if (onSaved) onSaved();
        onClose();
        return;
      }
      const res = await api.post("/home-assignments", body);
      data = res.data;
      toast.success(
        kind === "test"
          ? `Prov skapat — ${data.events_created} tillfällen genererade`
          : `Hemarbete skapat — ${data.events_created} läxor genererade`,
      );
      setCreatedAssignmentId(data.assignment?.id);
      try {
        const evRes = await api.get(`/events?type=homework&limit=300`);
        const own = evRes.data?.find((e) => e.home_assignment_id === data.assignment?.id);
        if (own) setCreatedEventId(own.id);
      } catch {}
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="add-home-assignment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-base flex items-center gap-2">
            <Sparkles size={16} className="text-[color:var(--jarvis)]" />
            {createdAssignmentId
              ? "Ladda upp material"
              : editingId
              ? (kind === "test" ? "Redigera prov" : "Redigera hemarbete")
              : (kind === "test" ? "Lägg till prov" : "Lägg till hemarbete")}
          </DialogTitle>
        </DialogHeader>

        {/* Typ-toggle (Läxa / Prov) — bara vid skapa nytt */}
        {!createdAssignmentId && !editingId && (
          <div className="mt-2 inline-flex rounded-full bg-[color:var(--surface-mist)] p-0.5 self-start">
            <button
              type="button"
              onClick={() => setKind("homework")}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                kind === "homework"
                  ? "bg-[#7CA858] text-white"
                  : "text-[color:var(--text-secondary)]"
              }`}
              data-testid="ha-kind-homework"
            >
              Hemarbete
            </button>
            <button
              type="button"
              onClick={() => setKind("test")}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                kind === "test"
                  ? "bg-[#E07A5F] text-white"
                  : "text-[color:var(--text-secondary)]"
              }`}
              data-testid="ha-kind-test"
            >
              Prov
            </button>
          </div>
        )}

        {/* Lista över befintliga hemarbeten — bara när inte i edit/create-skapat-läge */}
        {!createdAssignmentId && !editingId && existing.length > 0 && (
          <div className="mt-2 space-y-1.5 max-h-44 overflow-y-auto pr-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)]">
              Befintliga hemarbeten ({existing.length})
            </p>
            {existing.map((asg) => (
              <div
                key={asg.id}
                className="flex items-center justify-between gap-2 p-2 rounded-md bg-[color:var(--surface-mist)] text-xs"
                data-testid={`ha-existing-${asg.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{asg.subject} - {asg.title}</p>
                  <p className="text-[11px] text-[color:var(--text-tertiary)] truncate">
                    {(asg.child_names || []).join(", ")} · {asg.start_date}–{asg.end_date} · {asg.events_count || 0} läxor
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => loadForEdit(asg)}
                  className="p-1.5 rounded hover:bg-white text-[color:var(--text-secondary)]"
                  title="Redigera"
                  data-testid={`ha-edit-${asg.id}`}
                >
                  <Pencil size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(asg)}
                  className={
                    confirmDeleteId === asg.id
                      ? "px-2 py-1 rounded bg-rose-600 text-white text-[10px] font-semibold inline-flex items-center gap-1"
                      : "p-1.5 rounded hover:bg-white text-rose-600"
                  }
                  title={confirmDeleteId === asg.id ? "Klicka igen för att bekräfta" : "Radera"}
                  data-testid={`ha-delete-${asg.id}`}
                >
                  <Trash2 size={12} />
                  {confirmDeleteId === asg.id && "Säker?"}
                </button>
              </div>
            ))}
            <div className="border-t border-[color:var(--border-subtle)] pt-2 mt-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)]">
                Eller skapa nytt
              </p>
            </div>
          </div>
        )}

        {!createdAssignmentId ? (
          <div className="space-y-4 mt-2">
            {/* Titel */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)]">Titel</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="t.ex. Glosor v.21"
                data-testid="ha-title"
              />
            </div>

            {/* Ämne */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)]">Ämne</label>
              <select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded-md border border-[color:var(--border)] bg-white px-3 py-2 text-sm"
                data-testid="ha-subject"
              >
                {subjects.map((s) => (
                  <option key={s.code + s.name} value={s.name}>
                    {s.code} — {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Barn */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] flex items-center gap-1">
                <Users size={11} /> För vilket barn?
              </label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {(availableChildren || []).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleChild(c.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                      childIds.includes(c.id)
                        ? "bg-[color:var(--moss)] text-white border-[color:var(--moss)]"
                        : "bg-white text-[color:var(--text-secondary)] border-[color:var(--border)]"
                    }`}
                    data-testid={`ha-child-${c.id}`}
                  >
                    {c.name?.split(" ")[0] || c.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Datum — för prov: bara ETT datum. För hemarbete: från + till. */}
            {kind === "test" ? (
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] flex items-center gap-1">
                  <Calendar size={11} /> Provdatum
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setEndDate(e.target.value); }}
                  data-testid="ha-test-date"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] flex items-center gap-1">
                    <Calendar size={11} /> Från
                  </label>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="ha-start" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] flex items-center gap-1">
                    <Calendar size={11} /> Till
                  </label>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="ha-end" />
                </div>
              </div>
            )}

            {/* Veckodagar — bara för hemarbete (prov är 1 datum → 1 veckodag auto) */}
            {kind !== "test" && (
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)]">Veckodagar</label>
              <div className="flex gap-1 mt-1.5">
                {WEEKDAY_LABELS.map((lbl, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleWeekday(i)}
                    className={`flex-1 py-2 rounded-md text-xs font-medium border ${
                      weekdays.includes(i)
                        ? "bg-[color:var(--moss)] text-white border-[color:var(--moss)]"
                        : "bg-white text-[color:var(--text-secondary)] border-[color:var(--border)]"
                    }`}
                    data-testid={`ha-weekday-${i}`}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* Tid per pass — bara för hemarbete */}
            {kind !== "test" && (
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] flex items-center gap-1">
                <Clock size={11} /> Tid per pass (min)
              </label>
              <Input type="number" min="5" max="180" step="5" value={durationMin} onChange={(e) => setDurationMin(e.target.value)} data-testid="ha-duration" />
            </div>
            )}

            {/* Toggles */}
            <div className="space-y-2 pt-2 border-t border-[color:var(--border-subtle)]">
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={requiresSubmission} onChange={(e) => setRequiresSubmission(e.target.checked)} className="mt-1" data-testid="ha-requires-submission" />
                <span className="flex-1">
                  <span className="font-medium flex items-center gap-1"><CheckSquare size={12} /> Inlämning krävs</span>
                  <span className="text-xs text-[color:var(--text-tertiary)] block">Barnet ska visa upp resultat innan det räknas som klart</span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={reminderPush} onChange={(e) => setReminderPush(e.target.checked)} className="mt-1" data-testid="ha-reminder-push" />
                <span className="flex-1">
                  <span className="font-medium flex items-center gap-1"><Bell size={12} /> Påminnelse-push</span>
                  <span className="text-xs text-[color:var(--text-tertiary)] block">Notis kl 16:00 dagar då hemarbetet är schemalagt</span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={autoQuiz} onChange={(e) => setAutoQuiz(e.target.checked)} className="mt-1" data-testid="ha-auto-quiz" />
                <span className="flex-1">
                  <span className="font-medium flex items-center gap-1"><Sparkles size={12} /> Auto-quiz</span>
                  <span className="text-xs text-[color:var(--text-tertiary)] block">Edit genererar övningsfrågor när du laddat upp material</span>
                </span>
              </label>
            </div>

            {/* Fritext */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] flex items-center gap-1">
                <FileText size={11} /> Instruktion (fri text)
              </label>
              <Textarea
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder="t.ex. Läs sidorna 12–18 och svara på instuderingsfrågorna i häftet."
                className="min-h-[80px]"
                data-testid="ha-freetext"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            <p className="text-sm text-[color:var(--text-secondary)]">
              Hemarbetet är skapat. Vill du ladda upp material som Edit ska analysera?
              Materialet delas av alla genererade läxor.
            </p>
            {createdEventId && (
              <a
                href={`#material-${createdEventId}`}
                onClick={(e) => {
                  e.preventDefault();
                  onSaved?.(createdEventId);
                  onClose();
                }}
                className="block w-full text-center px-4 py-2.5 rounded-md bg-[color:var(--jarvis)] text-white text-sm font-medium"
                data-testid="ha-go-to-material"
              >
                Öppna första läxan & lägg till material →
              </a>
            )}
            <Button variant="outline" onClick={onClose} className="w-full" data-testid="ha-done">
              Klar — gör det senare
            </Button>
          </div>
        )}

        {!createdAssignmentId && (
          <DialogFooter className="mt-3 gap-2 sm:justify-between">
            {editingId ? (
              <Button
                variant="outline"
                onClick={async () => {
                  // Använd samma tvåstegs-confirm som listan: första klick = preppa,
                  // andra klick = radera. Returnerar true när raderingen lyckas.
                  const ok = await handleDelete({ id: editingId, title });
                  if (ok) onClose();
                }}
                disabled={saving}
                className={`text-[#DC2626] border-[#DC2626] hover:bg-[#FEF2F2] ${confirmDeleteId === editingId ? "bg-[#FEF2F2]" : ""}`}
                data-testid="ha-delete-editing"
              >
                <Trash2 size={14} className="mr-1.5" />
                {confirmDeleteId === editingId ? "Klicka igen för att radera" : "Radera"}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving} data-testid="ha-cancel">
                Avbryt
              </Button>
              <Button
                onClick={handleSave}
                disabled={!canSave || saving}
                className="bg-[color:var(--moss)]"
                data-testid="ha-save"
              >
                {saving ? (
                  <><Loader2 size={14} className="animate-spin mr-1.5" /> Sparar…</>
                ) : editingId ? (
                  "Spara ändringar"
                ) : (
                  kind === "test" ? "Skapa prov" : "Skapa hemarbete"
                )}
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
